import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { ErrorCode, Locale } from '@dashboard/contracts';
import en from './locales/en.json';
import fi from './locales/fi.json';
import sv from './locales/sv.json';

/**
 * Internationalization (architecture documentation section 16).
 *
 * All three catalogues are bundled rather than lazily fetched: together they
 * are a few kilobytes, and lazy-loading them would put a network round trip
 * between the shell rendering and it being readable.
 *
 * The backend never sends prose. It sends an error code and parameters, and
 * `translateErrorCode` below maps those to a sentence -- so a new locale needs
 * no backend change.
 */

export const SUPPORTED_LOCALES: Locale[] = ['en', 'fi', 'sv'];

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  fi: 'Suomi',
  sv: 'Svenska',
};

export async function initI18n(locale: Locale): Promise<void> {
  await i18next.use(initReactI18next).init({
    resources: {
      en: { translation: en },
      fi: { translation: fi },
      sv: { translation: sv },
    },
    lng: locale,
    fallbackLng: 'en',
    interpolation: {
      // React escapes for us; double-escaping would render entities literally.
      escapeValue: false,
    },
    returnNull: false,
  });
}

export async function changeLocale(locale: Locale): Promise<void> {
  await i18next.changeLanguage(locale);
  document.documentElement.lang = locale;
}

/**
 * Maps a machine-readable error code to a translated sentence, falling back to
 * a generic message for codes this build does not know. A newer backend adding
 * a code must not produce a blank error message in an older frontend.
 */
export function translateErrorCode(
  code: string | undefined,
  params?: Record<string, unknown>,
): string {
  const key = `errors.${code ?? 'unknown'}`;
  const translated = i18next.t(key, { ...params, defaultValue: '' });
  return translated || i18next.t('errors.unknown');
}

export function isKnownErrorCode(code: string): code is ErrorCode {
  return i18next.exists(`errors.${code}`);
}

/**
 * Names an audited action in the reader's language.
 *
 * Falls back to the raw action for the same reason `translateErrorCode` does:
 * the audit log is written by the backend, and a deployment running a newer API
 * than its frontend will serve actions this build has never heard of. A row
 * reading `cost_source.claim_added` is poor copy; a blank cell in an audit log
 * is a missing record, which is worse.
 */
export function translateAuditAction(action: string): string {
  return i18next.t(`audit.actions.${action}`, { defaultValue: action });
}

/* -------------------------------------------------------------------------- */
/* Locale-aware formatting                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Formatters are memoized: constructing an Intl formatter is comparatively
 * expensive, and a table of two hundred cells would otherwise build two hundred
 * of them on every render.
 */
const numberFormatters = new Map<string, Intl.NumberFormat>();
const dateFormatters = new Map<string, Intl.DateTimeFormat>();
const relativeFormatters = new Map<string, Intl.RelativeTimeFormat>();

function numberFormatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}:${JSON.stringify(options)}`;
  let formatter = numberFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options);
    numberFormatters.set(key, formatter);
  }
  return formatter;
}

function dateFormatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}:${JSON.stringify(options)}`;
  let formatter = dateFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateFormatters.set(key, formatter);
  }
  return formatter;
}

export function formatNumber(
  value: number,
  locale: string,
  options: Intl.NumberFormatOptions = {},
): string {
  return numberFormatter(locale, options).format(value);
}

/**
 * Timestamps cross the wire as UTC and are converted here, at the client
 * boundary, exactly as section 10.3 requires. The browser's zone is used
 * implicitly, which is what a user expects from an operations dashboard.
 */
export function formatDateTime(
  value: string | Date,
  locale: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return dateFormatter(locale, options).format(date);
}

/**
 * Formats an integer count of minor units as money.
 *
 * The amount arrives as a string of minor units, so the conversion to a
 * displayable number happens exactly once, here, at the boundary -- and the
 * currency code always travels with it. `Intl` knows how many minor units each
 * currency has, which is why the code is required rather than assumed: JPY has
 * none and TND has three, and hard-coding two would be wrong for both.
 *
 * A `Number` is unavoidable at this point because `Intl.NumberFormat` takes
 * one. That is safe here and nowhere else: this is the last step before pixels,
 * and nothing is computed from the result.
 */
export function formatMoney(
  minorAmount: string,
  currency: string,
  locale: string,
  options: { compact?: boolean } = {},
): string {
  const digits = minorUnitDigits(currency, locale);
  const value = Number(minorAmount) / 10 ** digits;
  if (!Number.isFinite(value)) return '—';

  return numberFormatter(locale, {
    style: 'currency',
    currency,
    // Money on a dashboard is read for magnitude, not reconciled to the cent,
    // and two decimals on a twelve-million figure is six characters of noise.
    // The entry detail view asks for the exact form.
    ...(options.compact
      ? { notation: 'compact', maximumFractionDigits: 1 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
  }).format(value);
}

/** The exact amount, to the currency's own precision. For detail views. */
export function formatMoneyExact(minorAmount: string, currency: string, locale: string): string {
  const digits = minorUnitDigits(currency, locale);
  const value = Number(minorAmount) / 10 ** digits;
  if (!Number.isFinite(value)) return '—';
  return numberFormatter(locale, { style: 'currency', currency }).format(value);
}

const minorDigitCache = new Map<string, number>();

/**
 * How many decimal places a currency has, resolved by `Intl` rather than from a
 * table this codebase would have to maintain.
 *
 * Exported because the amount *input* needs the same answer the amount display
 * uses: a field that accepts two decimals on a currency shown with none is a
 * field that silently changes what somebody typed.
 */
export function minorUnitDigits(currency: string, locale: string): number {
  const cached = minorDigitCache.get(currency);
  if (cached !== undefined) return cached;

  let digits = 2;
  try {
    digits =
      numberFormatter(locale, { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? 2;
  } catch {
    // An unknown currency code should degrade to a readable number rather than
    // throwing inside a render.
    digits = 2;
  }
  minorDigitCache.set(currency, digits);
  return digits;
}

/** A reporting period (`YYYY-MM`) as a localized month label. */
export function formatPeriod(period: string, locale: string): string {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return period;
  return dateFormatter(locale, { year: 'numeric', month: 'short' }).format(
    new Date(year, month - 1, 1),
  );
}

/** Calendar dates carry no time zone and must not be shifted by one. */
export function formatDate(value: string | null, locale: string): string {
  if (!value) return '—';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return '—';
  return dateFormatter(locale, { dateStyle: 'medium' }).format(new Date(year, month - 1, day));
}

/**
 * Today, as a calendar date.
 *
 * `new Date().toISOString().slice(0, 10)` is the obvious spelling, it was in
 * ten files, and it is wrong for part of every day: it is the date in UTC, not
 * the date on the reader's own calendar. East of Greenwich it still says
 * yesterday until the offset has passed -- until 03:00 in Helsinki, where this
 * product is used -- and west of it, it says tomorrow from the evening onwards.
 *
 * Tomorrow is the worse half. These values are defaults and `max` attributes on
 * date inputs: a progress report cannot be dated in the future, so a "today"
 * that lands on tomorrow is a default the API then refuses, and a `max` that
 * quietly permits what the server will reject.
 *
 * Beside `formatDate` because it is the same rule from the other side -- a
 * calendar date carries no time zone, so neither reading nor writing one may
 * pass through UTC.
 */
export function todayIsoDate(now: Date = new Date()): string {
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function formatRelativeSeconds(seconds: number, locale: string): string {
  // Memoized like the two formatters above, and for the same reason: this is
  // called once per row in the job and audit listings, and constructing an Intl
  // formatter is the expensive part. It was the one that got left out.
  let formatter = relativeFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    relativeFormatters.set(locale, formatter);
  }
  if (seconds < 60) return formatter.format(-Math.round(seconds), 'second');
  if (seconds < 3600) return formatter.format(-Math.round(seconds / 60), 'minute');
  if (seconds < 86_400) return formatter.format(-Math.round(seconds / 3600), 'hour');
  return formatter.format(-Math.round(seconds / 86_400), 'day');
}

export { i18next };
